import styled from "styled-components";
import * as React from 'react';
import { rawColors } from "../../designSystem/colors/color";
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { TextField } from '../../designSystem/formField/text/TextField';
import { Label } from '../../designSystem/Label';
import { Button } from '../../designSystem/button/Button';
import { ButtonRow } from '../../designSystem/button/ButtonRow';
import { WhiteLink } from '../../designSystem/Link';
import { MessageCard } from '../../designSystem/MessageCard';
import { P } from '../../designSystem/typography/Body';

type LoginPageViewProps = {
    cardHeader: string;
    cardBackgroundColor?: string;

    onSubmit: React.FormEventHandler<HTMLFormElement>;

    email: string;
    onEmailChange: (value: string) => void;
    password: string;
    onPasswordChange: (value: string) => void;

    organisation?: string;
    onOrganisationChange?: (value: string) => void;

    forgotPasswordHref?: string;
    errorMessage?: string;
    submitText?: string;
}

export function LoginPageView(props: LoginPageViewProps): JSX.Element {
    const {
        cardHeader,
        onSubmit,
        email,
        onEmailChange,
        password,
        onPasswordChange,
        organisation,
        onOrganisationChange,
        forgotPasswordHref,
        errorMessage,
        submitText = 'Log ind',
    } = props;

    return (
        <LoginLayout>
            <CardLoginWrapper>
                <Flex flexDirection="column">
                    <SpecialHeaderWrapper>
                        <AmorgBrand>
                            <img src="/images/amorg_logo_blue.svg" alt="AMORG Logo" width={32} height={24} />
                            {cardHeader}
                        </AmorgBrand>
                    </SpecialHeaderWrapper>
                    <CardLogin cardBackgroundColor={props.cardBackgroundColor}>
                        <form autoComplete="on" onSubmit={onSubmit}>
                            <Flex flexDirection="column" gap={16}>
                                {onOrganisationChange && (
                                    <Flex flexDirection="column">
                                        <Label color={rawColors.white}>Organisation</Label>
                                        <TextField
                                            dataCy='site-input'
                                            value={organisation || ''}
                                            onChange={onOrganisationChange}
                                            autoComplete="on"
                                            placeholder="Organisation"
                                            validator={() => ({ valid: true })}
                                        />
                                    </Flex>
                                )}

                                <Flex flexDirection="column">
                                    <Label color={rawColors.white}>E-mail</Label>
                                    <TextField
                                        dataCy="email-input"
                                        value={email}
                                        onChange={onEmailChange}
                                        autoComplete="on"
                                        placeholder="E-mail"
                                        validator={() => ({ valid: true })}
                                    />
                                </Flex>

                                <Flex flexDirection='column'>
                                    <Label color={rawColors.white}>Adgangskode</Label>
                                    <TextField
                                        dataCy="pw-input"
                                        type="password"
                                        value={password}
                                        onChange={onPasswordChange}
                                        autoComplete="on"
                                        placeholder="Adgangskode"
                                        validator={() => ({ valid: true })}
                                    />
                                    {forgotPasswordHref && (
                                        <ButtonRow variant={"centered"}>
                                            <WhiteLink  
                                                href={forgotPasswordHref}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >Glemt adgangskode ?</WhiteLink>
                                        </ButtonRow>
                                    )}
                                </Flex>

                                {errorMessage && (
                                    <div>
                                        <MessageCard variant="error"><P>{errorMessage}</P></MessageCard>
                                    </div>
                                )}

                                <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
                                    <Button variant="filled"
                                        dataCy="submit"
                                        text={submitText} />
                                </Flex>
                            </Flex>
                        </form>
                    </CardLogin>
                </Flex>
            </CardLoginWrapper>
        </LoginLayout>
    );
}

const LoginLayout = styled.div`
    width:100%;
    min-height: calc(100vh - 60px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;

    > * {
        width: 100%;
        max-width: 400px;
    }
`;

const CardLoginWrapper = styled.div`
    width:100%;
    max-width:400px;
`;

const SpecialHeaderWrapper = styled.div`
    background: white;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    display: flex;
    justify-content: space-between;
    padding: .5rem 1rem;
`;

const CardLogin = styled.div<{cardBackgroundColor?: string}>`
    background: ${props => props.cardBackgroundColor || 'gray'};
    padding: 1rem;
`;

export const AmorgBrand = styled.div`
    color: ${rawColors.gray.dark};
    display: flex;
    flex-direction: row;
    align-items: center;
    font-size: 32px;
    font-weight: bold;
    gap: 8px;
`;
